import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import TextField from "@/components/TextField";
import DateInput from "@/components/DateInput";
import Select from "@/components/Select";
import PageHeading from "@/components/PageHeading/PageHeading";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { useNavigate } from "@/router";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { formatEnumLabel } from "../_utils";
import { useCanShowCommissionPlatformBool, useRequireCommissionPlatform } from "../useCommissionPlatformGate";
import { COMMISSION_PARTNERS_QUERY } from "../partners/queries";
import { COMMISSION_PROGRAMS_QUERY } from "../programs/queries";
import { CREATE_COMMISSION_CAMPAIGN_MUTATION } from "./mutations";
import type { CommissionCampaignStatus, CommissionPayoutCadence } from "./types";

interface CreateCampaignFormValues {
  partnerId: string;
  programId: string;
  editionYear: string;
  status: CommissionCampaignStatus;
  payoutCadence: CommissionPayoutCadence;
  startsAt: string;
  endsAt: string;
}

const statusOptions: CommissionCampaignStatus[] = ["DRAFT", "ACTIVE", "CLOSED"];
const payoutCadenceOptions: CommissionPayoutCadence[] = ["QUARTERLY"];

export default function CreateCommissionCampaignPage() {
  usePageTitle("/billing/finance/commission/campaigns/new", "New Campaign");
  useRequireCapability({ domain: DOMAIN.BENEFIT_PROGRAMS, permission: PERMISSION.manageGrpCommission });
  useRequireCommissionPlatform();
  const canQuery = useCanShowCommissionPlatformBool();
  const navigate = useNavigate();
  const [createCampaign, { loading }] = useMutation(CREATE_COMMISSION_CAMPAIGN_MUTATION);

  const { data: partnersData } = useQuery(COMMISSION_PARTNERS_QUERY, {
    variables: { first: 100 },
    skip: !canQuery
  });
  const { data: programsData } = useQuery(COMMISSION_PROGRAMS_QUERY, {
    variables: { first: 20 },
    skip: !canQuery
  });

  const { control, handleSubmit, getValues } = useForm<CreateCampaignFormValues>({
    defaultValues: {
      partnerId: "",
      programId: "",
      editionYear: String(new Date().getFullYear()),
      status: "DRAFT",
      payoutCadence: "QUARTERLY",
      startsAt: `${new Date().getFullYear()}-01-01`,
      endsAt: `${new Date().getFullYear()}-12-31`
    }
  });

  const partnerOptions = useMemo(
    () =>
      (partnersData?.commissionPartners.nodes ?? []).map((partner) => ({
        value: partner.id,
        label: partner.name
      })),
    [partnersData]
  );

  const programOptions = useMemo(
    () =>
      (programsData?.commissionPrograms.nodes ?? []).map((program) => ({
        value: program.id,
        label: program.name
      })),
    [programsData]
  );

  const statusSelectOptions = useMemo(
    () => statusOptions.map((value) => ({ value, label: formatEnumLabel(value) })),
    []
  );

  const payoutCadenceSelectOptions = useMemo(
    () => payoutCadenceOptions.map((value) => ({ value, label: formatEnumLabel(value) })),
    []
  );

  const onSubmit = handleSubmit(async (values) => {
    try {
      await createCampaign({
        variables: {
          input: {
            partnerId: values.partnerId,
            programId: values.programId,
            editionYear: Number(values.editionYear),
            status: values.status,
            payoutCadence: values.payoutCadence,
            startsAt: `${values.startsAt}T00:00:00Z`,
            endsAt: `${values.endsAt}T23:59:59Z`
          }
        }
      });
      showSnackbar("Campaign created.");
      navigate("/billing/finance/commission/campaigns");
    } catch {
      showErrorSnackbar("Unable to create campaign. Try again or contact support.");
    }
  });

  return (
    <FocusedPageLayout
      backLink={{ to: "/billing/finance/commission/campaigns" }}
      stickyActions
      clusterActions={[
        {
          text: "Create campaign",
          onClick: onSubmit,
          loading,
          testId: "create-campaign"
        }
      ]}
    >
      <PageHeading title="New Campaign" />
      <p className="pb-6 text-body-medium text-sys-brand-on-surface-variant">
        Link a sponsor partner to a commission program edition for a plan year.
      </p>

      <form className="flex max-w-xl flex-col gap-6" onSubmit={onSubmit}>
        <Controller
          name="partnerId"
          control={control}
          rules={{ required: "Partner is required" }}
          render={({ field, fieldState }) => (
            <Select
              label="Partner"
              value={field.value}
              onSelect={field.onChange}
              options={partnerOptions}
              error={!!fieldState.error}
              supportingText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="programId"
          control={control}
          rules={{ required: "Program is required" }}
          render={({ field, fieldState }) => (
            <Select
              label="Program"
              value={field.value}
              onSelect={field.onChange}
              options={programOptions}
              error={!!fieldState.error}
              supportingText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="editionYear"
          control={control}
          rules={{ required: "Edition year is required" }}
          render={({ field, fieldState }) => (
            <TextField
              label="Edition year"
              type="number"
              value={field.value}
              onChange={field.onChange}
              error={!!fieldState.error}
              supportingText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <Select label="Status" value={field.value} onSelect={field.onChange} options={statusSelectOptions} />
          )}
        />

        <Controller
          name="payoutCadence"
          control={control}
          render={({ field }) => (
            <Select
              label="Payout cadence"
              value={field.value}
              onSelect={field.onChange}
              options={payoutCadenceSelectOptions}
            />
          )}
        />

        <Controller
          name="startsAt"
          control={control}
          rules={{ required: "Start date is required" }}
          render={({ field, fieldState }) => (
            <DateInput
              label="Starts"
              value={field.value || null}
              onChange={(next) => field.onChange(next ?? "")}
              onBlur={field.onBlur}
              error={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              supportingText="YYYY-MM-DD"
            />
          )}
        />

        <Controller
          name="endsAt"
          control={control}
          rules={{
            required: "End date is required",
            validate: (value) => value >= (getValues("startsAt") || "") || "End date must be on or after the start date"
          }}
          render={({ field, fieldState }) => (
            <DateInput
              label="Ends"
              value={field.value || null}
              onChange={(next) => field.onChange(next ?? "")}
              onBlur={field.onBlur}
              error={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              supportingText="YYYY-MM-DD"
            />
          )}
        />
      </form>
    </FocusedPageLayout>
  );
}
